import React from 'react';
import { View, Text, StyleSheet, Modal, Pressable, useWindowDimensions } from 'react-native';
import { Paprika, PaprikaShadows } from '../../styles/theme';
import { useTranslation } from 'react-i18next';

const MENU_WIDTH = 236;

/** Screen-space position of the ⋯ button that opened the menu. */
export interface MenuAnchor {
  /** Distance from the top of the window to the top of the menu card. */
  top: number;
  /** Distance from the right of the window to the right edge of the menu card. */
  right: number;
}

export interface OverflowMenuItem {
  key: string;
  label: string;
  onPress: () => void;
  /** Renders in the destructive tone and above a hairline. */
  destructive?: boolean;
}

interface OverflowMenuProps {
  visible: boolean;
  onClose: () => void;
  items: OverflowMenuItem[];
  anchor?: MenuAnchor;
}

const DEFAULT_ANCHOR: MenuAnchor = { top: 64, right: 16 };

/**
 * The ⋯ menu that absorbs everything the actions bar used to carry: rating a
 * cook, editing, opening the source, deleting. Modal-based so it works the same
 * on web and native, with a full-bleed backdrop that closes on tap.
 */
export const OverflowMenu: React.FC<OverflowMenuProps> = ({ visible, onClose, items, anchor }) => {
  const { t } = useTranslation();
  const { width, height } = useWindowDimensions();

  if (items.length === 0) return null;

  const target = anchor ?? DEFAULT_ANCHOR;
  // Keep the card fully on screen even when the anchor sits near an edge.
  const right = Math.min(Math.max(target.right, 8), Math.max(width - MENU_WIDTH - 8, 8));
  const top = Math.min(Math.max(target.top, 8), Math.max(height - 60, 8));

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose} accessibilityLabel={t('editRecipe.closeMenu')} />
      <View style={[styles.card, { top, right }]}>
        {items.map((item, index) => (
          <Pressable
            key={item.key}
            style={({ pressed }) => [
              styles.row,
              item.destructive && index > 0 && styles.rowSeparated,
              pressed && styles.rowPressed,
            ]}
            onPress={() => {
              onClose();
              item.onPress();
            }}
            accessibilityRole="button"
            testID={`overflow-${item.key}`}
          >
            <Text style={[styles.label, item.destructive && styles.labelDestructive]}>
              {item.label}
            </Text>
          </Pressable>
        ))}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  card: {
    position: 'absolute',
    width: MENU_WIDTH,
    backgroundColor: Paprika.surfaceLowest,
    borderWidth: 1,
    borderColor: Paprika.hairline,
    borderRadius: 14,
    padding: 6,
    ...PaprikaShadows.modal,
  },
  row: {
    paddingVertical: 9,
    paddingHorizontal: 11,
    borderRadius: 9,
  },
  rowSeparated: {
    borderTopWidth: 1,
    borderTopColor: Paprika.hairline,
    marginTop: 4,
  },
  rowPressed: {
    backgroundColor: Paprika.surfaceLow,
  },
  label: {
    fontFamily: 'Inter-Medium',
    fontSize: 13.5,
    color: Paprika.textPrimary,
  },
  labelDestructive: {
    color: Paprika.destructive,
  },
});

export default OverflowMenu;
